/**
 * Conversation compression & summarization for AI chat.
 * Automatically summarizes conversation at 15 messages to preserve context
 * while reducing token usage.
 *
 * Strategy:
 * - Keep full history in UI (for user reference)
 * - Send compressed context to API (for token efficiency)
 * - Store summary in AI memory system (for cross-conversation context)
 *
 * Integration with conversationMemory.ts:
 * - Extract user intents and completed actions from messages
 * - Save summary to AI memory for cross-conversation context
 * - Preserve pinned messages across summarization
 */

import { extractUserIntents, extractCompletedActions, saveConversationSummary } from "./conversationMemory"
import type { ChatMessage } from "./useAiChat"

const AUTO_SUMMARIZE_AT_MESSAGES = 15
const KEEP_RECENT_MESSAGES = 5

export interface CompressionState {
  isSummarized: boolean
  summary?: string
  intents?: string[]
  completedActions?: string[]
  /**
   * How many messages (from the start of the conversation) the summary covers. What is sent to the AI is the
   * summary plus every message after those - see apiMessages.ts buildRequestMessages. (This used to be a list of
   * messages frozen when the summary was made, which left every later message out.)
   */
  summarizedCount?: number
}

/**
 * Check if conversation should be summarized
 */
export function shouldCompress(messages: ChatMessage[]): boolean {
  // Count actual exchanges (user + assistant pairs)
  const exchanges = messages.length / 2
  return exchanges >= AUTO_SUMMARIZE_AT_MESSAGES
}

/**
 * Extract last N messages (full ChatMessage format)
 */
export function extractRecentMessages(messages: ChatMessage[], count = KEEP_RECENT_MESSAGES): ChatMessage[] {
  return messages.slice(-count)
}

/**
 * Estimate token count (rough: ~4 tokens per word)
 */
export function estimateTokenCount(text: string): number {
  const wordCount = text.split(/\s+/).length
  return Math.ceil(wordCount * 1.3) // ~1.3 tokens per word on average
}

/**
 * Calculate token savings from compression
 */
export function calculateTokenSavings(
  originalMessages: ChatMessage[],
  compressedMessages: Array<{ role: "user" | "assistant"; content?: string }>,
): number {
  const original = originalMessages.reduce((sum, m) => sum + estimateTokenCount(m.content || ""), 0)
  const compressed = compressedMessages.reduce((sum, m) => sum + estimateTokenCount(m.content || ""), 0)
  return Math.max(0, original - compressed)
}

/**
 * Prepare messages for summarization API call (first N messages excluding recent)
 */
export function extractMessagesToSummarize(
  messages: ChatMessage[],
  keepRecent = KEEP_RECENT_MESSAGES,
): ChatMessage[] {
  const totalToCompress = messages.length - keepRecent
  return messages.slice(0, Math.max(0, totalToCompress))
}

/**
 * Save compression state to AI memory system for cross-conversation context.
 * Called after successful summarization to persist the summary and metadata.
 */
export async function persistCompressionToMemory(
  conversationId: string | null,
  messages: ChatMessage[],
  compression: CompressionState,
): Promise<boolean> {
  if (!conversationId || !compression.isSummarized || !compression.summary) {
    return false
  }

  try {
    // Extract user intents and completed actions
    const userIntents = extractUserIntents(messages)
    const completedActions = extractCompletedActions(messages)

    // Get pinned message IDs if any
    const pinnedMessageIds = messages
      .filter((m) => "isPinned" in m && (m as unknown as Record<string, unknown>).isPinned === true)
      .map((m) => m.id)

    // Save to AI memory system
    return await saveConversationSummary(
      conversationId,
      messages,
      compression.summary,
      userIntents,
      completedActions,
      pinnedMessageIds,
    )
  } catch (e) {
    console.warn("[conversationCompression] Failed to persist to memory:", e)
    return false
  }
}
